// Boat physics: ferries a freshly-spawned (or respawned) tank from its
// DeepSea start tile toward land. The rider can now steer (turn left/right)
// and adjust speed (Faster/Slower); originally the boat drifted on auto, but
// player input feels much better. The boat keeps the canonical "auto-drift
// at default speed if the player does nothing" behaviour so newly-spawned
// players still arrive at shore even if they never touch the keys.
//
// Damage: shells now sink boats (BOAT_FULL_ARMOUR hits to destroy). When
// the boat sinks, the rider drowns — the tank dies and respawns on a
// fresh boat after the usual TANK_DEATH_WAIT.

import {
  BOAT_ACCEL_RATE,
  BOAT_MAX_SPEED,
  BOAT_TURN_RATE,
  MAP_SIZE,
  Terrain,
  TERRAIN_MASK,
} from '@bolo2k/shared';
import { hasKey, type TankInput, TankKey, EMPTY_INPUT } from './input.js';
import { dirVector, snapDir, tileX, tileY } from './math.js';
import { pickupPillIfDestroyed } from './movement.js';
import type { Boat, GameState, Tank } from './types.js';

// Tile types that "stop" the boat (it explodes here, dropping the tank).
// Water tiles let the boat keep going.
function isLandfall(code: number): boolean {
  if (code === Terrain.DeepSea) return false;
  if (code === Terrain.River) return false;
  if (code === Terrain.Boat) return false;
  return true;
}

// Tiles the boat physically cannot cross. Used by the axis-separated step
// below to refuse a wall-bound move rather than barrelling into it.
// Without this guard, a boat driven at full speed into a wall at an angle
// shoved the tank inside the Building tile (where Tank turn-rate is 0, so
// the player could not escape) — the "stuck in a wall" bug.
function isImpassableForBoat(code: number): boolean {
  return (
    code === Terrain.Building ||
    code === Terrain.HalfBuilding ||
    code === Terrain.Pillbox
  );
}

export function tickBoats(state: GameState, inputs: ReadonlyMap<number, TankInput>): void {
  if (state.boats.length === 0) return;
  const survivors: Boat[] = [];
  for (const boat of state.boats) {
    const tank = state.tanks.find((t) => t.id === boat.riderTankId);
    if (!tank || !tank.alive) {
      // Rider missing or dead — drop the boat.
      if (tank) tank.inBoatId = 0;
      continue;
    }

    const input = inputs.get(tank.id) ?? EMPTY_INPUT;

    // --- Steering -----------------------------------------------------
    const turnLeft = hasKey(input, TankKey.TurnLeft);
    const turnRight = hasKey(input, TankKey.TurnRight);
    if (turnLeft || turnRight) {
      const delta = turnLeft ? -BOAT_TURN_RATE : BOAT_TURN_RATE;
      let nd = boat.dir + delta;
      if (nd < 0) nd += 256;
      else if (nd >= 256) nd -= 256;
      boat.dir = nd;
    }

    // --- Throttle -----------------------------------------------------
    if (hasKey(input, TankKey.Faster)) {
      boat.speed = Math.min(BOAT_MAX_SPEED, boat.speed + BOAT_ACCEL_RATE);
    } else if (hasKey(input, TankKey.Slower)) {
      boat.speed = Math.max(0, boat.speed - BOAT_ACCEL_RATE);
    }

    // --- Drift --------------------------------------------------------
    // Axis-separated: each axis only commits if the destination tile under
    // that axis is not an impassable wall. Mirrors tank movement so a
    // boat brushing past a wall slides along it instead of beaching the
    // tank inside the wall. Both axes blocked = boat stalls (speed kept;
    // player can turn).
    if (boat.speed > 0) {
      const { dx, dy } = dirVector(snapDir(boat.dir));
      const stepX = dx * boat.speed;
      const stepY = dy * boat.speed;
      const candidateX = clamp(boat.x + stepX);
      const candidateY = clamp(boat.y + stepY);
      const curTileX = tileX(boat.x);
      const curTileY = tileY(boat.y);
      const candTileX = tileX(candidateX);
      const candTileY = tileY(candidateY);
      const yTileCell = state.terrain[candTileY * MAP_SIZE + curTileX]! & TERRAIN_MASK;
      if (!isImpassableForBoat(yTileCell)) boat.y = candidateY;
      const xTileCell = state.terrain[curTileY * MAP_SIZE + candTileX]! & TERRAIN_MASK;
      if (!isImpassableForBoat(xTileCell)) boat.x = candidateX;
    }

    // Tank position tracks the boat.
    tank.x = boat.x;
    tank.y = boat.y;
    tank.dir = boat.dir;

    // Look at the tile the boat is now over.
    const tx = tileX(boat.x);
    const ty = tileY(boat.y);
    const cell = state.terrain[ty * MAP_SIZE + tx]! & TERRAIN_MASK;

    // Pick up any destroyed-pill entity sitting at this water tile. Mirrors
    // WinBolo `tankCheckPillCapture` being called from BOTH tankMoveOnLand
    // and tankMoveOnBoat (tank.c:1342, 1625) — a tank in a boat that sails
    // over a water-borne pill recovers it. Without this, pills dropped on
    // river/deep-sea by a dying tank would be unrecoverable.
    pickupPillIfDestroyed(state, tank, tx, ty);

    if (isLandfall(cell)) {
      // eslint-disable-next-line no-console
      console.log(JSON.stringify({
        severity: 'INFO',
        message: 'boat beached',
        tankId: tank.id,
        tile: { x: tx, y: ty },
        terrainCode: cell,
      }));
      tank.inBoatId = 0;
      // Hand off the boat's full speed to the tank. WinBolo `tankMoveOnBoat`
      // (tank.c:1364) just flips onBoat = FALSE without touching speed —
      // the tank inherits the boat's velocity and the natural per-terrain
      // decel slows it down once it's on land. Axis-separated collision
      // makes the "punch through a building" failure mode impossible
      // anyway (impassable tiles refuse the move axis-by-axis).
      tank.speed = boat.speed;
      continue;
    }

    // Safety: if the boat somehow got pushed off the map.
    if (tx <= 0 || tx >= MAP_SIZE - 1 || ty <= 0 || ty >= MAP_SIZE - 1) {
      tank.inBoatId = 0;
      continue;
    }

    survivors.push(boat);
  }
  state.boats = survivors;
}

function clamp(v: number): number {
  if (v < 0) return 0;
  if (v > 0xffff) return 0xffff;
  return v;
}

/** Result of one tick of client-side boat prediction. `beached` flips true
 *  the moment the boat's predicted position lands on a non-water tile —
 *  the caller (predictor) flips its shadow's `inBoatId` to 0 so the next
 *  tick predicts tank movement instead. Authoritative landfall (and the
 *  associated tank.speed handoff, pillbox pickup, sound emit) is still
 *  the server's job; the client just stops pushing the boat forward. */
export interface BoatPredictResult {
  beached: boolean;
}

/** Client-side mirror of one boat tick. Mutates `boat` in place — steer,
 *  throttle, axis-separated drift, landfall classification. No state side
 *  effects (no pillbox pickup, no sound emit) — those are the server's
 *  responsibility. Same physics constants and same axis-separated wall
 *  collision as `tickBoats`, so client prediction and server simulation
 *  produce identical positions when fed the same input. */
export function predictBoatMovement(
  terrain: Uint8Array,
  boat: { x: number; y: number; dir: number; speed: number },
  input: TankInput,
): BoatPredictResult {
  // Steer.
  const turnLeft = hasKey(input, TankKey.TurnLeft);
  const turnRight = hasKey(input, TankKey.TurnRight);
  if (turnLeft || turnRight) {
    const delta = turnLeft ? -BOAT_TURN_RATE : BOAT_TURN_RATE;
    let nd = boat.dir + delta;
    if (nd < 0) nd += 256;
    else if (nd >= 256) nd -= 256;
    boat.dir = nd;
  }

  // Throttle.
  if (hasKey(input, TankKey.Faster)) {
    boat.speed = Math.min(BOAT_MAX_SPEED, boat.speed + BOAT_ACCEL_RATE);
  } else if (hasKey(input, TankKey.Slower)) {
    boat.speed = Math.max(0, boat.speed - BOAT_ACCEL_RATE);
  }

  // Drift — axis-separated.
  if (boat.speed > 0) {
    const { dx, dy } = dirVector(snapDir(boat.dir));
    const stepX = dx * boat.speed;
    const stepY = dy * boat.speed;
    const candidateX = clamp(boat.x + stepX);
    const candidateY = clamp(boat.y + stepY);
    const curTileX = tileX(boat.x);
    const curTileY = tileY(boat.y);
    const candTileX = tileX(candidateX);
    const candTileY = tileY(candidateY);
    const yTileCell = terrain[candTileY * MAP_SIZE + curTileX]! & TERRAIN_MASK;
    if (!isImpassableForBoat(yTileCell)) boat.y = candidateY;
    const xTileCell = terrain[curTileY * MAP_SIZE + candTileX]! & TERRAIN_MASK;
    if (!isImpassableForBoat(xTileCell)) boat.x = candidateX;
  }

  // Landfall classification.
  const tx = tileX(boat.x);
  const ty = tileY(boat.y);
  const cell = terrain[ty * MAP_SIZE + tx]! & TERRAIN_MASK;
  return { beached: isLandfall(cell) };
}
